import { z } from 'zod';
import { ACCENT_COLORS, boardDocSchema } from './types.js';
import { opSchema } from './ops.js';
/** Shapes crossing the network, shared so client and server cannot drift. */
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
/**
 * 10 characters minimum. Long-but-simple beats short-but-gnarly for real
 * accounts, so there is no character-class rule — just a floor and a ceiling
 * (the ceiling keeps scrypt from becoming a DoS vector).
 */
export const passwordSchema = z.string().min(10).max(200);
export const displayNameSchema = z.string().trim().min(1).max(80);
export const signupSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    name: displayNameSchema.optional(),
});
export const loginSchema = z.object({ email: emailSchema, password: passwordSchema });
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({ password: passwordSchema });
export const prefsSchema = z.object({
    /** Set once the getting-started checklist has been completed or dismissed. */
    onboarded: z.boolean().nullable().optional(),
    accent: z.enum(ACCENT_COLORS).nullable().optional(),
    dotGrid: z.boolean().nullable().optional(),
    showMinimap: z.boolean().nullable().optional(),
    presence: z.boolean().nullable().optional(),
});
/**
 * Changing a password while signed in.
 *
 * `currentPassword` is optional because an account created through single
 * sign-on has no password to confirm — for those this sets the first one.
 */
export const changePasswordSchema = z.object({
    currentPassword: z.string().max(200).optional(),
    newPassword: passwordSchema,
});
/**
 * Closing an account.
 *
 * `confirm` must be the account's own email address — deliberate friction in
 * front of something irreversible, and the standard way to ask for it.
 */
export const deleteAccountSchema = z.object({
    confirm: z.string().trim().max(254),
    password: z.string().max(200).optional(),
});
export const updateMeSchema = z.object({
    name: displayNameSchema.optional(),
    prefs: prefsSchema.optional(),
});
export const createBoardSchema = z.object({ name: z.string().trim().min(1).max(200).optional() });
export const updateBoardSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    isPublic: z.boolean().optional(),
});
export const inviteRoleSchema = z.enum(['editor', 'viewer']);
export const createInviteSchema = z.object({ role: inviteRoleSchema });
export const addMemberSchema = z.object({ email: emailSchema, role: inviteRoleSchema });
/** Upload limits, shared so the client can reject a file before sending it. */
export const MAX_UPLOAD_BYTES = 12 * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
/**
 * Portable board file.
 *
 * Images are bundled rather than left as URLs, so a board exported from one
 * instance imports into another with its pictures intact. They travel alongside
 * the document instead of inlined into it, because the document schema caps
 * `src` well below the size of a real photograph.
 */
export const BOARD_FILE_VERSION = 1;
export const boardFileAssetSchema = z.object({
    /** Referenced from an item's `src` as `asset:<ref>`. */
    ref: z.string().min(1).max(64),
    name: z.string().max(300),
    mime: z.enum(ALLOWED_IMAGE_TYPES),
    /** Base64, no data-URI prefix. */
    data: z.string().min(1).max(20_000_000),
});
export const boardFileSchema = z.object({
    scribbleplot: z.literal(BOARD_FILE_VERSION),
    name: z.string().trim().min(1).max(200),
    exportedAt: z.string().max(40).optional(),
    doc: boardDocSchema,
    assets: z.array(boardFileAssetSchema).max(500).default([]),
});
/** Placeholder an exported item's `src` carries in place of a URL. */
export const ASSET_REF_PREFIX = 'asset:';
/* ------------------------------------------------------------------ *
 * Realtime protocol
 * ------------------------------------------------------------------ */
/** Cursor position in world space, plus the peer's current selection size. */
export const cursorSchema = z.object({
    x: z.number().finite(),
    y: z.number().finite(),
});
export const clientMsgSchema = z.discriminatedUnion('t', [
    /** Submit ops. `seq` is echoed back so the sender can ignore its own edits. */
    z.object({ t: z.literal('ops'), seq: z.number().int().nonnegative(), ops: z.array(opSchema).min(1).max(200) }),
    z.object({ t: z.literal('cursor'), pos: cursorSchema.nullable() }),
    /** Re-sync after a dropped connection or a rejected op. */
    z.object({ t: z.literal('resync') }),
    z.object({ t: z.literal('ping') }),
]);
/** Deterministic peer color so the same person keeps the same cursor hue. */
export const PEER_COLORS = ['#F49E9E', '#8EC1F2', '#9BD8AC', '#F6D56A', '#D9B8F5', '#22D3EE', '#FFB454'];
export function peerColor(id) {
    let hash = 0;
    for (let i = 0; i < id.length; i++)
        hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    return PEER_COLORS[hash % PEER_COLORS.length];
}
//# sourceMappingURL=api.js.map